import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNotification, GlobalUser } from '../../types';
import { supabase } from '../../supabaseClient';

export interface NotificationMenuProps {
    notifications: AppNotification[];
    setNotifications: React.Dispatch<React.SetStateAction<AppNotification[]>>;
    currentUser?: GlobalUser;
    onClose?: () => void;
}

export const NotificationMenu: React.FC<NotificationMenuProps> = ({
    notifications,
    setNotifications,
    currentUser,
    onClose
}) => {
    const navigate = useNavigate();
    const [filterTab, setFilterTab] = useState<'todas' | 'pendientes'>('todas');

    // Rule 3: Only show notifications that belong to the current user or 'all' if user has project access
    const isUserRecipient = (n: AppNotification): boolean => {
        if (!currentUser) return true;
        // User assigned directly
        if (n.userId === currentUser.id || n.userId === currentUser.email) return true;
        // Global broadcast
        if (n.userId === 'all') {
            if (currentUser.globalRole === 'Administrador') return true;
            if (n.projectId) {
                return currentUser.assignedProjects.some(ap => ap.projectId === n.projectId);
            }
            return true;
        }
        return false;
    };

    const userNotifications = useMemo(() => {
        return notifications
            .filter(isUserRecipient)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [notifications, currentUser]);

    const unreadNotifications = useMemo(() => {
        return userNotifications.filter(n => !n.isRead);
    }, [userNotifications]);

    const readNotifications = useMemo(() => {
        return userNotifications.filter(n => n.isRead).slice(0, 20);
    }, [userNotifications]);

    const displayedList = filterTab === 'pendientes'
        ? unreadNotifications
        : [...unreadNotifications, ...readNotifications];

    const markAsRead = (id: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setNotifications(prev =>
            prev.map(n => (n.id === id ? { ...n, isRead: true } : n))
        );
        // Persist change via secure RPC in background
        (async () => {
            try {
                await supabase.rpc('mark_notification_as_read', { p_notification_id: id });
            } catch { }
        })();
    };

    const markAllAsRead = () => {
        setNotifications(prev =>
            prev.map(n => (isUserRecipient(n) ? { ...n, isRead: true } : n))
        );
        (async () => {
            try {
                await supabase.rpc('mark_all_notifications_as_read');
            } catch { }
        })();
    };

    const clearReadNotifications = () => {
        setNotifications(prev =>
            prev.filter(n => !(isUserRecipient(n) && n.isRead))
        );
        (async () => {
            try {
                await supabase.rpc('clear_user_notification_history');
            } catch { }
        })();
    };

    const handleNotificationClick = (n: AppNotification) => {
        markAsRead(n.id);
        if (n.link) {
            navigate(n.link);
            if (onClose) onClose();
        }
    };

    const formatDate = (dateStr: string) => {
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            const now = new Date();
            const isToday = d.toDateString() === now.toDateString();
            const time = d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });

            if (isToday) {
                return `Hoy ${time}`;
            }
            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            if (d.toDateString() === yesterday.toDateString()) {
                return `Ayer ${time}`;
            }
            return `${d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' })} ${time}`;
        } catch {
            return dateStr;
        }
    };

    const getTypeBadge = (type: AppNotification['type']) => {
        switch (type) {
            case 'quality':
                return {
                    label: 'Calidad',
                    icon: 'verified',
                    cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                };
            case 'planning':
                return {
                    label: 'Planificación',
                    icon: 'calendar_month',
                    cls: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800'
                };
            case 'prevention':
                return {
                    label: 'Prevención',
                    icon: 'shield',
                    cls: 'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300 border-orange-200 dark:border-orange-800'
                };
            case 'payments':
                return {
                    label: 'Estados de Pago',
                    icon: 'payments',
                    cls: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                };
            default:
                return {
                    label: 'Sistema',
                    icon: 'notifications',
                    cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                };
        }
    };

    return (
        <div className="w-[94vw] sm:w-[440px] bg-white dark:bg-[#1a2634] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[85vh] animate-in fade-in duration-200 z-50">
            {/* Header */}
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-[#16202c] flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-indigo-600 dark:text-indigo-400 text-[22px]">notifications</span>
                    <h3 className="font-black text-slate-900 dark:text-white text-sm tracking-tight">Notificaciones</h3>
                    {unreadNotifications.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-red-500 text-white shadow-sm">
                            {unreadNotifications.length} pendiente{unreadNotifications.length > 1 ? 's' : ''}
                        </span>
                    )}
                </div>

                {unreadNotifications.length > 0 && (
                    <button
                        onClick={markAllAsRead}
                        className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 flex items-center gap-1 transition-colors px-2 py-1 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/30 cursor-pointer"
                        title="Marcar todas como leídas"
                    >
                        <span className="material-symbols-outlined text-[14px]">done_all</span>
                        Marcar todo leído
                    </button>
                )}
            </div>

            {/* Filter Tabs */}
            <div className="flex border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-[#1a2634] px-4 pt-2 gap-2">
                <button
                    onClick={() => setFilterTab('todas')}
                    className={`pb-2 text-xs font-bold transition-all relative ${
                        filterTab === 'todas'
                            ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                            : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                    }`}
                >
                    Todas ({userNotifications.length})
                </button>
                <button
                    onClick={() => setFilterTab('pendientes')}
                    className={`pb-2 text-xs font-bold transition-all relative ${
                        filterTab === 'pendientes'
                            ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                            : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                    }`}
                >
                    Pendientes ({unreadNotifications.length})
                </button>
            </div>

            {/* Notification List */}
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/60">
                {displayedList.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                        <div className="size-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 mb-3">
                            <span className="material-symbols-outlined text-[26px]">notifications_none</span>
                        </div>
                        <p className="text-sm font-bold text-slate-700 dark:text-slate-300">Sin notificaciones pendientes</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Estás al día con tus revisiones y tareas de obra.</p>
                    </div>
                ) : (
                    displayedList.map(n => {
                        const badge = getTypeBadge(n.type);
                        const isUnread = !n.isRead;
                        return (
                            <div
                                key={n.id}
                                onClick={() => handleNotificationClick(n)}
                                className={`p-3.5 transition-all relative cursor-pointer group ${
                                    isUnread
                                        ? 'bg-indigo-50/20 hover:bg-indigo-50/40 dark:bg-indigo-950/15 dark:hover:bg-indigo-950/30'
                                        : 'bg-white hover:bg-slate-50 dark:bg-[#1a2634] dark:hover:bg-slate-800/40 opacity-80 hover:opacity-100'
                                }`}
                            >
                                {/* Unread indicator bar */}
                                {isUnread && (
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600 rounded-r"></div>
                                )}

                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0 space-y-1">
                                        {/* Metadata Tags Row */}
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {/* Type Badge */}
                                            <span className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border ${badge.cls}`}>
                                                <span className="material-symbols-outlined text-[11px]">{badge.icon}</span>
                                                {badge.label}
                                            </span>

                                            {/* Project / Obra Badge */}
                                            {n.projectName && (
                                                <span className="inline-flex items-center gap-1 text-[9px] font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 truncate max-w-[150px]">
                                                    <span className="material-symbols-outlined text-[10px] text-slate-400">apartment</span>
                                                    {n.projectName}
                                                </span>
                                            )}

                                            {/* Attention Required Chip */}
                                            {n.requiresAttention && isUnread && (
                                                <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800 px-1.5 py-0.5 rounded shadow-xs">
                                                    <span className="size-1.5 rounded-full bg-rose-500 animate-ping"></span>
                                                    Requiere Atención
                                                </span>
                                            )}

                                            {/* Timestamp */}
                                            <span className="text-[9px] text-slate-400 dark:text-slate-500 ml-auto font-medium">
                                                {formatDate(n.date)}
                                            </span>
                                        </div>

                                        {/* Title */}
                                        <h4 className={`text-xs font-bold leading-tight ${isUnread ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300'}`}>
                                            {n.title}
                                        </h4>

                                        {/* Description */}
                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug line-clamp-2">
                                            {n.description}
                                        </p>

                                        {/* Action link hint */}
                                        {n.link && (
                                            <div className="pt-1 flex items-center gap-1 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 group-hover:underline">
                                                <span>Ver elemento relacionado</span>
                                                <span className="material-symbols-outlined text-[12px]">arrow_forward</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Mark as read button */}
                                    {isUnread && (
                                        <button
                                            onClick={(e) => markAsRead(n.id, e)}
                                            className="size-7 rounded-full bg-slate-100 hover:bg-indigo-100 dark:bg-slate-800 dark:hover:bg-indigo-900/40 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-300 flex items-center justify-center transition-colors shrink-0 cursor-pointer"
                                            title="Marcar como leída"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">check</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Footer with Operational Disclaimer & Clear Read button */}
            <div className="p-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-[#16202c] flex items-center justify-between text-[10px] text-slate-400">
                <span className="truncate max-w-[220px]" title="Marcar como leída no altera el estado de la tarea ni del protocolo">
                    ℹ️ Lectura informativa, no altera flujos
                </span>
                {readNotifications.length > 0 && (
                    <button
                        onClick={clearReadNotifications}
                        className="text-[11px] font-bold text-slate-500 hover:text-red-500 transition-colors cursor-pointer"
                    >
                        Limpiar historial
                    </button>
                )}
            </div>
        </div>
    );
};

export const NotificationBell: React.FC<{
    notifications: AppNotification[];
    setNotifications: React.Dispatch<React.SetStateAction<AppNotification[]>>;
    currentUser?: GlobalUser;
}> = ({ notifications, setNotifications, currentUser }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Compute unread count scoped to this user
    const unreadCount = useMemo(() => {
        return notifications.filter(n => {
            if (n.isRead) return false;
            if (!currentUser) return true;
            if (n.userId === currentUser.id || n.userId === currentUser.email) return true;
            if (n.userId === 'all') {
                if (currentUser.globalRole === 'Administrador') return true;
                if (n.projectId) {
                    return currentUser.assignedProjects.some(ap => ap.projectId === n.projectId);
                }
                return true;
            }
            return false;
        }).length;
    }, [notifications, currentUser]);

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative flex items-center justify-center size-9 rounded-full text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 transition-all cursor-pointer"
                title={unreadCount > 0 ? `${unreadCount} notificaciones pendientes` : 'Notificaciones'}
                aria-label="Notificaciones"
            >
                <span className="material-symbols-outlined text-[22px]">notifications</span>
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center border-2 border-white dark:border-[#1a2634] shadow-sm animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <>
                    <div
                        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-xs md:bg-transparent"
                        onClick={() => setIsOpen(false)}
                    />
                    <div className="fixed left-3 right-3 top-16 md:absolute md:left-auto md:right-0 md:top-full md:mt-2 z-50 origin-top-right">
                        <NotificationMenu
                            notifications={notifications}
                            setNotifications={setNotifications}
                            currentUser={currentUser}
                            onClose={() => setIsOpen(false)}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
